/**
 * A column of items arriving, or leaving, as one wave — for the reader's rail, whose items number in
 * the hundreds.
 *
 * Not the `Reveal`/`Pop` components: those are a React component per item, and the rail's bars and
 * lines are already rendered by the time anything wants to animate them. These run on the items
 * as they are, with the Web Animations API — opacity and scale, which the compositor animates off
 * the main thread — and only on the items actually on screen. The rest are never touched: a bar
 * a thousand pixels down the rail animating in unseen is work for nothing.
 */

import { DURATION, EASE, WAVE, waveDelay } from "@/lib/motion"

/** Marks each item of a rail that arrives and leaves in the wave. */
export const RAIL_ITEM_ATTRIBUTE = "data-rail-item"


const EASING = `cubic-bezier(${EASE.join(", ")})`

/** The same small pop as `Pop` in components/ui/motion. */
const HIDDEN: Keyframe = { opacity: 0, transform: "scale(0.9)" }
const SHOWN: Keyframe = { opacity: 1, transform: "none" }

/**
 * The items inside `viewport`, in order.
 *
 * The items run down the page in order, so the first one in view is found by a binary search — a
 * handful of measurements however long the rail — and the rest by walking on until one starts
 * below the bottom. Measuring every item would make the browser lay out the parts of the rail it
 * is skipping off screen.
 */
export function visibleItems(viewport: DOMRect, items: HTMLElement[]): HTMLElement[] {
  let low = 0
  let high = items.length
  while (low < high) {
    const middle = (low + high) >> 1
    if (items[middle].getBoundingClientRect().bottom <= viewport.top) low = middle + 1
    else high = middle
  }
  const visible: HTMLElement[] = []
  for (let i = low; i < items.length; i++) {
    const bounds = items[i].getBoundingClientRect()
    if (bounds.top >= viewport.bottom) break
    if (bounds.height > 0) visible.push(items[i])
  }
  return visible
}

/** The items marked in `root` that are on screen within it. */
export function railItemsInView(root: HTMLElement): HTMLElement[] {
  const items = Array.from(root.querySelectorAll<HTMLElement>(`[${RAIL_ITEM_ATTRIBUTE}]`))
  return visibleItems(root.getBoundingClientRect(), items)
}

/** Pop the items in, top to bottom. Each stays hidden until its turn (`fill: backwards`). */
export function cascadeIn(items: HTMLElement[]): void {
  items.forEach((item, i) => {
    item.animate([HIDDEN, SHOWN], {
      duration: DURATION.base * 1000,
      delay: waveDelay(i, items.length) * 1000,
      easing: EASING,
      fill: "backwards",
    })
  })
}

/**
 * Pop the items out, top to bottom and quicker than they came, resolving once the last has gone.
 * They stay hidden after (`fill: forwards`), for whatever removes them next.
 */
export function cascadeOut(items: HTMLElement[]): Promise<void> {
  const animations = items.map((item, i) =>
    item.animate([SHOWN, HIDDEN], {
      duration: DURATION.quick * 1000,
      delay: (waveDelay(i, items.length, WAVE.exitSpread) - waveDelay(0, items.length, WAVE.exitSpread)) * 1000,
      easing: "ease-in",
      fill: "forwards",
    }),
  )
  return Promise.all(animations.map((animation) => animation.finished)).then(
    () => undefined,
    () => undefined,
  )
}

/*
  A panel sliding in from an edge of the screen and back out: the rail's overlay from the right on a
  screen too narrow for its column, the recent history from the bottom (see ReaderLayout and
  HistoryPanel). A transform, so the panel moves on the compositor and the page under it is never
  laid out again.
*/
export type PanelEdge = "right" | "bottom"

const OFF_EDGE: Record<PanelEdge, Keyframe> = {
  right: { transform: "translateX(calc(100% + 16px))" },
  bottom: { transform: "translateY(calc(100% + 16px))" },
}
const IN_PLACE: Keyframe = { transform: "none" }

export function slideIn(surface: HTMLElement, edge: PanelEdge = "right"): void {
  surface.animate([OFF_EDGE[edge], IN_PLACE], { duration: DURATION.base * 1000, easing: EASING })
}

/** Slide the panel out, resolving once it has gone; it stays out after (`fill: forwards`). */
export function slideOut(surface: HTMLElement, edge: PanelEdge = "right"): Promise<void> {
  return surface
    .animate([IN_PLACE, OFF_EDGE[edge]], { duration: DURATION.quick * 1000, easing: "ease-in", fill: "forwards" })
    .finished.then(
      () => undefined,
      () => undefined,
    )
}
